import { useEffect, useState } from 'react';
import { Icons } from '../Icons.jsx';
import { speak } from '../../utils/speech.js';
import { tapLight } from '../../utils/haptics.js';
import { BALL } from '../../theme.js';
import { t } from '../../i18n.js';
import { Sentry } from '../../sentry.js';

const T = BALL;

export default function Cloze({ exercise, lang, onResult, locked }) {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setSelected(null);
  }, [exercise]);

  function pick(opt) {
    if (locked || selected) return;
    tapLight();
    setSelected(opt);
    const correct = opt === exercise.correct;
    Sentry.addBreadcrumb({ category: 'exercise', message: 'cloze-submit', data: { correct } });
    onResult({ correct, given: opt, expected: exercise.correct });
  }

  // Split the prompt on the blank marker so we can render the gap inline.
  const parts = exercise.promptWithBlank.split('___');

  return (
    <div style={{ padding: '4px 22px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{
          fontSize: 11, fontWeight: 800, color: T.blue, letterSpacing: '0.12em',
        }}>{t('clozeHeader', lang)}</div>
        <h2 style={{
          fontSize: 24, fontWeight: 900, color: T.ink, margin: '6px 0 0', lineHeight: 1.15,
        }}>{t('clozeTitle', lang)}</h2>
      </div>

      <div style={{
        background: T.bgAlt, border: `2px solid ${T.hairline}`,
        borderRadius: 16, padding: '16px 18px',
        boxShadow: `0 3px 0 ${T.hairline}`,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{
            flex: 1, fontSize: 22, fontWeight: 800, color: T.ink, lineHeight: 1.4,
          }}>
            {parts.map((part, i) => (
              <span key={i}>
                {part}
                {i < parts.length - 1 && (
                  <span style={{
                    display: 'inline-block', verticalAlign: 'baseline',
                    minWidth: '4ch', margin: '0 4px',
                    borderBottom: `2px dashed ${T.hairline}`,
                    color: selected ? T.primary : 'transparent',
                    fontWeight: 900,
                  }}>
                    {selected || ' '}
                  </span>
                )}
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={() => speak(exercise.speakText)}
            aria-label={t('tapToHear', lang)}
            style={{
              appearance: 'none', border: 'none', cursor: 'pointer',
              width: 44, height: 44, borderRadius: '50%',
              background: T.blue, color: '#fff', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 3px 0 ${T.blueDeep}`,
            }}
          >
            {Icons.speaker('#fff')}
          </button>
        </div>
        {exercise.translation && (
          <div style={{ fontSize: 13, color: T.inkSoft, fontWeight: 600 }}>
            {exercise.translation}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {exercise.options.map((opt) => {
          const isSel = selected === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => pick(opt)}
              disabled={locked || !!selected}
              style={{
                appearance: 'none',
                cursor: locked || selected ? 'not-allowed' : 'pointer',
                background: T.bgAlt,
                border: `2px solid ${isSel ? T.blue : T.hairline}`,
                boxShadow: `0 3px 0 ${isSel ? T.blueDeep : T.hairline}`,
                borderRadius: 12,
                padding: '14px 12px',
                fontWeight: 800, fontSize: 17,
                color: isSel ? T.blue : T.primary,
                fontFamily: 'inherit',
                minHeight: 56,
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
